<template>
  <div>
    <gg-loading v-if="loading && !accountMailboxes.length" :loading="true" type="card" />

    <!-- Nothing but the empty state while the account has no mailboxes
         (AM-1107); the shell owns the create modal. -->
    <feature-setup
      v-else-if="!loadFailed && !accountMailboxes.length"
      feature="accounts"
      @create="$emit('create-mailbox')"
    />

    <gg-card v-else flat>
      <!-- Add Mailbox sits here rather than in the account header (AM-1109),
           like Create on the Forwarders and Auto Reply tabs. -->
      <gg-card-header>
        <div class="d-flex align-center flex-wrap">
          <span class="mr-auto">Manage Mailboxes</span>
          <gg-btn secondary small class="my-1" @click="$emit('create-mailbox')">
            <v-icon left small>mdi-plus</v-icon>Add Mailbox
          </gg-btn>
        </div>
      </gg-card-header>
      <gg-card-text>
        <v-alert v-if="loadFailed" type="error" text dense class="mb-4">
          We couldn't load the mailboxes on this account.
          <gg-btn small tertiary class="ml-2" @click="load">Try again</gg-btn>
        </v-alert>

        <mailboxes-table
          :items="accountMailboxes"
          :loading="loading"
          @action="handleAction"
        />
      </gg-card-text>
    </gg-card>

    <v-dialog v-model="modals.password" hide-overlay max-width="500px">
      <change-password
        v-if="modals.password && selected"
        :email="selected.email"
        :domain="selected.domain"
        @close="closeModal('password')"
      />
    </v-dialog>

    <!-- Mounted once a row is picked and fed the store's current copy of it,
         so a change made in Settings shows up without reopening. -->
    <template v-if="current">
      <mailbox-settings-modal
        :is-active="modals.settings"
        :mailbox="current"
        @changed="refreshService(service.id)"
        @close="modals.settings = false"
      />

      <create-forwarder-modal
        :is-active="modals.forwarder"
        :mailbox="current"
        @close="modals.forwarder = false"
      />

      <add-alias-modal
        :is-active="modals.alias"
        :mailbox="current"
        @created="refreshService(service.id)"
        @close="modals.alias = false"
      />

      <auto-reply-modal
        :is-active="modals.autoreply"
        :mailbox="current"
        @close="modals.autoreply = false"
      />

      <catch-all-modal
        :is-active="modals.catchall"
        :mailbox="current"
        @close="modals.catchall = false"
      />
    </template>

    <delete-email
      v-if="selected"
      :is-active="modals.remove"
      :email="selected.email"
      @close="closeModal('remove')"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import MailboxesTable from '@/components/Email/MailboxesTable'
import FeatureSetup from '@/components/Email/FeatureSetup'
import ChangePassword from '@/components/Services/Hosting/Shared/Email/Accounts/Modals/ChangePassword'
import DeleteEmail from '@/components/Services/Hosting/Shared/Email/Accounts/Modals/DeleteEmail'
import MailboxSettingsModal from '@/components/Email/MailboxSettingsModal'
import CreateForwarderModal from '@/components/Email/CreateForwarderModal'
import AddAliasModal from '@/components/Email/AddAliasModal'
import AutoReplyModal from '@/components/Email/AutoReplyModal'
import CatchAllModal from '@/components/Email/CatchAllModal'
import { launch_webmail, WEBMAIL_ROUNDCUBE } from '@/helpers/helpers'

/**
 * The Accounts tab: one account's mailboxes.
 *
 * The shared modals act on `store.getters.hosting`, which EmailAccountView has
 * already pointed at this account's service before rendering this tab.
 */
export default {
  name: 'AccountMailboxesView',
  components: {
    MailboxesTable,
    FeatureSetup,
    ChangePassword,
    DeleteEmail,
    MailboxSettingsModal,
    CreateForwarderModal,
    AddAliasModal,
    AutoReplyModal,
    CatchAllModal,
  },
  // Passed down by EmailAccountView.
  props: {
    service: {
      type: Object,
      required: true,
    },
  },
  data () {
    return {
      loading: false,
      loadFailed: false,
      selected: null,
      modals: {
        password: false,
        settings: false,
        forwarder: false,
        alias: false,
        autoreply: false,
        catchall: false,
        remove: false,
      },
    }
  },
  computed: {
    ...mapGetters('emailHub', ['mailboxFor', 'scopedMailboxes']),
    // `selected` is the row as it was clicked; a re-read of the service
    // replaces the store's objects, so look the mailbox up again.
    current () {
      if (!this.selected) return null
      return this.mailboxFor(this.selected.serviceId, this.selected.email) || this.selected
    },
    // The main domain, or an addon domain opened as its own account.
    scopeDomain () {
      return this.$route.params.maindomain
    },
    accountMailboxes () {
      return this.scopedMailboxes(this.service.id, this.scopeDomain)
    },
  },
  methods: {
    ...mapActions('emailHub', ['loadServiceMailboxes', 'refreshService']),
    /**
     * Re-read on every visit rather than trusting the store: the hosting Email
     * section edits the same mailboxes and does not tell this module.
     */
    async load () {
      this.loading = true
      this.loadFailed = false
      try {
        await this.loadServiceMailboxes(this.service)
      } catch (e) {
        this.loadFailed = true
      } finally {
        this.loading = false
      }
    },
    handleAction ({ action, mailbox }) {
      this.selected = mailbox

      if (action === 'webmail') {
        launch_webmail(mailbox.serviceId, mailbox.email, WEBMAIL_ROUNDCUBE)
        return
      }

      if (action === 'delete') {
        this.modals.remove = true
      } else if (action in this.modals) {
        this.modals[action] = true
      }
    },
    /**
     * Refresh on close rather than on success: DeleteEmail defines an
     * `action-completed` event but never emits it, so closing is the only
     * reliable signal. Costs one reload when the customer cancels.
     */
    closeModal (name) {
      this.modals[name] = false
      if (name === 'remove') {
        this.refreshService(this.service.id)
      }
    },
  },
  created () {
    this.load()
  },
}
</script>
